import React from "react";

import { useTranslation } from "react-i18next";

import { WalletIcon } from "@components/icons/WalletIcon";
import WalletTag from "@components/shared/WalletTag";
import MyBetsAutosellButton from "@features/my-bets/desktop/common/MyBetsAutosellButton";
import MyBetsCashoutButton from "@features/my-bets/desktop/common/MyBetsCashoutButton";
import ShareBetSlipButton from "@features/my-bets/desktop/common/ShareBetSlipButton";
import { Bet } from "@models";
import MyBetsViewStore from "@state/stores/my-bets/MyBetsViewStore";
import { getCurrentCulture } from "@utils";
import { hasActiveEvents } from "@utils/my-bets/helpers";
import { shouldShowBetSlipStatus } from "@utils/offer/shouldShowBetSlipStatus";
import { shouldUseSlipStatusInstead } from "@utils/offer/shouldUseSlipStatusInstead";
import classNames from "classnames";
import { observer } from "mobx-react";

import { DisplayDate } from ".";
import BetItemPaymentDetail from "./BetItemPaymentDetail";
import OpenSingleBet from "./OpenSingleBet";
import FreebetTag from "@components/shared/FreebetTag";

export default observer(function MutliItem(props: {
	bet: Bet;
	isFull: boolean;
	myBetsViewStore: MyBetsViewStore;
}) {
	const { t } = useTranslation("mybets");

	if (props.bet.betSlipOffers == null) {
		return null;
	}

	const titleClasses = classNames("widget--bets__head", [
		`widget--bets__head--${getCurrentCulture()}`,
	]);

	if (props.bet.isRemoved) {
		return null;
	}
	let betStatus = props.bet.betStatus;
	let tagClasses = `widget--bets__tag tag tag--tny tag--bet tag--bet--${betStatus?.abrv}`;

	if (shouldUseSlipStatusInstead(props.bet)) {
		betStatus = props.bet.betSlipStatus;
		tagClasses = `widget--bets__tag tag tag--tny tag--slip tag--slip--${betStatus?.abrv}`;
	}
	return (
		<div key={props.bet.id} className="widget--bets__item">
			<div className={titleClasses}>
				<div
					className="widget--bets__status"
					title={
						props.bet.betStatus?.name +
						" - " +
						props.bet.betSlipType.name
					}
				>
					<span className={tagClasses}>
						{props.bet.betStatus?.name}
						{props.bet.isBonus && (
							<> ({t<string>("COMMON.BONUS")})</>
						)}
					</span>

					{shouldShowBetSlipStatus(props.bet) && (
						<span
							className={
								"widget--bets__tag tag tag--tny tag--slip tag--slip--" +
								props.bet.betSlipStatus.abrv
							}
						>
							{props.bet.betSlipStatus.name}
						</span>
					)}

                    <FreebetTag 
                        isFreebet={props.bet.isFreebet} 
                    />

					<WalletTag
						isWalletManuallyDisabled={
							props.bet.isWalletManuallyDisabled || false
						}
					/>

					<WalletIcon
						bettingAccountTypeAbrv={
							props.bet.bettingAccountType?.abrv
						}
						classList="widget--bets__wallet"
						isWalletManuallyDisabled={
							props.bet.isWalletManuallyDisabled || false
						}
					/>
				</div>

				<ShareBetSlipButton bet={props.bet} isMini={true} isIconOnly />

				{hasActiveEvents(props.bet) ? (
					<button
						type="button"
						className="widget--bets__use btn btn--square btn--square--xsml btn--primary--inverted"
						onClick={() =>
							props.myBetsViewStore.openReuseBetSlipModal(
								props.bet
							)
						}
						title={t<string>("REUSE.BUTTON_LABEL")}
					>
						<i className="u-icon u-icon--tny u-icon--refresh" />
					</button>
				) : null}
			</div>

			<div className="widget--bets__info">
				<div className="widget--bets__title">
					{props.bet.hasBetBuilder && (
						<i className="widget--bets__icon u-icon u-icon--sml u-icon--bb--active" />
					)}
					<span className="widget--bets__title__inline">
						{props.bet.formattedBetSystemString}
					</span>
				</div>
			</div>

			<div className="widget--bets__row">
				<div className="widget--bets__col">
					<div className="widget--bets__label">
						{t<string>("mybets:ITEM.INFO_PAYMENT_LABEL")}:
					</div>
					<div className="widget--bets__amount">
						{props.bet.displayPayment}
					</div>
				</div>
				<div className="widget--bets__col">
					<BetItemPaymentDetail
						betStatusAbrv={props.bet.betStatus?.abrv || ""}
						betSlipStatusAbrv={props.bet.betSlipStatus.abrv || ""}
						payout={props.bet.displayPayout}
						maxGain={props.bet.displayMaxGain}
					/>
				</div>
				<div className="widget--bets__col">
					<div className="widget--bets__label">
						{t<string>("mybets:ITEM.ADDITIONAL_MAX_COEFFICIENT")}:
					</div>
					<div className="widget--bets__amount">
						{props.bet.maxCoefficient.toFixed(2)}
					</div>
				</div>
			</div>

			<div className="widget--bets__actions">
				<MyBetsCashoutButton
					bet={props.bet}
					openCashoutModal={
						props.myBetsViewStore.cashoutStore.openCashoutModal
					}
				/>
				<MyBetsAutosellButton
					bet={props.bet}
					openAutosellModal={
						props.myBetsViewStore.cashoutStore.openAutosellModal
					}
				/>
			</div>

			<div className="widget--bets__footer">
				<div className="widget--bets__timestamp">
					<DisplayDate betDateTimeStamp={props.bet.dateCreated} />
				</div>

				<OpenSingleBet
					bet={props.bet}
					myBetsViewStore={props.myBetsViewStore}
				/>
			</div>
		</div>
	);
});
